import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { resizeForUpload } from "@/lib/image-resize";
import type { components, paths } from "@breenova/api-client";

type Schemas = components["schemas"];

export type BirdSummary = Schemas["BirdSummary"];
export type PedigreeNode = Schemas["GetBirdPedigreePedigreeNodeDto"];
export type PedigreeResponse = Schemas["GetBirdPedigreeResponse"];
export type PairingScore = Schemas["GetPedigreeCoefficientResponse"];

/**
 * Ahnentafel positions indexed for lookup: the proband is 1, and the parents of `n` are
 * `2n` and `2n+1`. A position that is missing is an ancestor nobody recorded, which is
 * exactly what the tree renders as a dashed placeholder.
 */
export function indexPedigree(nodes: readonly PedigreeNode[]): Map<number, PedigreeNode> {
  return new Map(nodes.map((node) => [node.position, node]));
}
export type BirdDetail = Schemas["BirdDetail"];
export type Sex = Schemas["Sex"];
export type BirdStatus = Schemas["BirdStatus"];

/** Taken from the query parameter itself — the sort order is not a response schema. */
export type BirdSort = NonNullable<
  NonNullable<paths["/api/v1/birds"]["get"]["parameters"]["query"]>["Sort"]
>;

type JsonBody<P, M extends keyof P> = P[M] extends {
  requestBody: { content: { "application/json": infer B } };
}
  ? B
  : never;

export type CreateBirdBody = JsonBody<paths["/api/v1/birds"], "post">;
export type UpdateBirdBody = JsonBody<paths["/api/v1/birds/{id}"], "put">;

/**
 * Drops undefined entries.
 *
 * `exactOptionalPropertyTypes` is on, which distinguishes "absent" from "present and
 * undefined" — and the generated query types only allow absent. Building the object then
 * pruning it keeps the call sites readable.
 */
type Defined<T> = { [K in keyof T]?: Exclude<T[K], undefined> };

function defined<T extends Record<string, unknown>>(value: T): Defined<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as Defined<T>;
}

/** Every filter the chip row and the toolbar can set. */
export interface BirdFilters {
  search?: string | undefined;
  speciesId?: string | undefined;
  sex?: Sex | undefined;
  status?: BirdStatus | undefined;
  mutationId?: string | undefined;
  locationId?: string | undefined;
  hatchYear?: number | undefined;
  sort?: BirdSort | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

/**
 * Query keys are built from the filters so that changing one refetches and the previous
 * result stays cached — flipping a chip back and forth should not re-hit the network.
 */
export const birdKeys = {
  all: ["birds"] as const,
  list: (filters: BirdFilters) => ["birds", "list", filters] as const,
  detail: (id: string) => ["birds", "detail", id] as const,
  availableRings: (year?: number) => ["ring-batches", "available", year ?? null] as const,
  locations: (onlyCages: boolean) => ["locations", { onlyCages }] as const,
  reference: ["reference"] as const,
  pedigree: (id: string, generations: number) => ["birds", "pedigree", id, generations] as const,
  coefficient: (sireId: string, damId: string) =>
    ["pedigree", "coefficient", sireId, damId] as const,
};

function unwrap<T>(data: T | undefined, error: unknown, what: string): T {
  if (error || data === undefined) {
    throw new Error(`Could not load ${what}.`);
  }

  return data;
}

export function useBirds(filters: BirdFilters) {
  return useQuery({
    queryKey: birdKeys.list(filters),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/birds", {
        params: {
          query: defined({
            Search: filters.search || undefined,
            SpeciesId: filters.speciesId,
            Sex: filters.sex,
            Status: filters.status,
            MutationId: filters.mutationId,
            LocationId: filters.locationId,
            HatchYear: filters.hatchYear,
            Sort: filters.sort,
            Page: filters.page ?? 1,
            PageSize: filters.pageSize ?? 50,
          }),
        },
      });

      return unwrap(data, error, "the bird list");
    },

    // Keeps the previous page on screen while the next loads, so flipping a chip does
    // not blank the list and bounce the scroll position back to the top.
    placeholderData: (previous) => previous,
  });
}

export function useBird(id: string | undefined): UseQueryResult<BirdDetail> {
  return useQuery({
    queryKey: birdKeys.detail(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/birds/{id}", {
        params: { path: { id: id! } },
      });

      return unwrap(data, error, "that bird") as BirdDetail;
    },
  });
}

/**
 * The ancestor tree behind a bird, with its inbreeding coefficient.
 *
 * The depth is part of the key, so switching between three and five generations caches
 * both rather than refetching each time the breeder flips back.
 */
export function useBirdPedigree(id: string | undefined, generations = 3) {
  return useQuery({
    queryKey: birdKeys.pedigree(id ?? "", generations),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/birds/{id}/pedigree", {
        params: { path: { id: id! }, query: { generations } },
      });

      return unwrap(data, error, "the pedigree");
    },
  });
}

/** Scores a proposed pairing. Idle until both birds are chosen. */
export function usePedigreeCoefficient(sireId?: string, damId?: string) {
  return useQuery({
    queryKey: birdKeys.coefficient(sireId ?? "", damId ?? ""),
    enabled: Boolean(sireId && damId),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/pedigree/coefficient", {
        params: { query: { sireId: sireId!, damId: damId! } },
      });

      return unwrap(data, error, "the pairing score");
    },
  });
}

/** Species, mutations, ring sizes and federations. Seeded, so effectively static. */
export function useReferenceData() {
  return useQuery({
    queryKey: birdKeys.reference,
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/reference");
      return unwrap(data, error, "reference data");
    },
  });
}

export function useLocations(onlyCages = false) {
  return useQuery({
    queryKey: birdKeys.locations(onlyCages),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/locations", {
        params: { query: { OnlyCages: onlyCages } },
      });

      return unwrap(data, error, "your locations");
    },
  });
}

/** The unused rings feeding the ring-number autocomplete. */
export function useAvailableRingNumbers(year?: number, enabled = true) {
  return useQuery({
    queryKey: birdKeys.availableRings(year),
    enabled,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/ring-batches/available", {
        params: { query: defined({ Year: year, Take: 50 }) },
      });

      return unwrap(data, error, "your ring batches");
    },
  });
}

export function useCreateBird() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateBirdBody) => {
      const { data, error } = await api.POST("/api/v1/birds", { body });

      if (error) {
        throw new Error(problemMessage(error, "Could not save the bird."));
      }

      return data;
    },
    onSuccess: () => {
      // The list and the ring autocomplete are both stale now: a new bird changes what
      // the list shows and takes a ring out of circulation.
      void queryClient.invalidateQueries({ queryKey: birdKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["ring-batches"] });
    },
  });
}

export function useUpdateBird(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateBirdBody) => {
      const { data, error } = await api.PUT("/api/v1/birds/{id}", {
        params: { path: { id } },
        body,
      });

      if (error) {
        throw new Error(problemMessage(error, "Could not save the bird."));
      }

      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: birdKeys.all });
    },
  });
}

export function useDeleteBird() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE("/api/v1/birds/{id}", {
        params: { path: { id } },
      });

      if (error) {
        throw new Error(problemMessage(error, "Could not delete the bird."));
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: birdKeys.all });
    },
  });
}

/** Mirrors `UploadBirdPhoto.MaxUploadBytes` — checked here so a 40 MB file never leaves the phone. */
export const MAX_PHOTO_BYTES = 15 * 1024 * 1024;

/**
 * Uploads one photo and makes it the bird's face.
 *
 * The generated body type only knows about `file` — `caption` and `makePrimary` are read
 * from the form by the endpoint rather than bound, so they are not in the OpenAPI document.
 * The serializer builds the form itself, and the JSON content type the client sets by
 * default is removed so the browser can write the multipart boundary.
 */
export function useUploadBirdPhoto(birdId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (picked: File) => {
      // Shrunk first, so the size check judges what would actually be sent: a 20 MB
      // original is fine if it comes out at 500 KB.
      const file = await resizeForUpload(picked);

      if (file.size > MAX_PHOTO_BYTES) {
        throw new Error(
          `That photo is ${Math.ceil(file.size / (1024 * 1024))} MB. The limit is ${
            MAX_PHOTO_BYTES / (1024 * 1024)
          } MB.`,
        );
      }

      const { data, error } = await api.POST("/api/v1/birds/{id}/photos", {
        params: { path: { id: birdId } },
        // The placeholder satisfies the generated type; the serializer ignores it.
        body: { file: "" },
        bodySerializer: () => {
          const form = new FormData();
          form.append("file", file);
          form.append("makePrimary", "true");
          return form;
        },
        headers: { "Content-Type": null },
      });

      if (error) {
        throw new Error(problemMessage(error, "Could not upload the photo."));
      }

      return data;
    },
    onSuccess: () => {
      // Both the detail and the list carry the primary photo's URL.
      void queryClient.invalidateQueries({ queryKey: birdKeys.all });
    },
  });
}

/**
 * Pulls a readable message out of an RFC 9457 problem-details body.
 *
 * Worth the effort here rather than showing a generic failure: the domain rejections this
 * screen produces are things like "NL-1234-25-007 is already on another bird", which tell
 * the breeder exactly what to change.
 */
export function problemMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const problem = error as {
      detail?: string;
      title?: string;
      errors?: Record<string, string[]>;
    };

    if (problem.errors) {
      const first = Object.values(problem.errors).flat()[0];
      if (first) {
        return first;
      }
    }

    return problem.detail ?? problem.title ?? fallback;
  }

  return fallback;
}
